import { useState } from 'react';

export interface ListFilterState {
  query: string;
  from: string;
  to: string;
  status: string;
}

export const EMPTY_FILTER: ListFilterState = { query: '', from: '', to: '', status: '' };

/** فى الذاكرة فقط: يتمسح مع إغلاق البرنامج، ولا يُخزَّن على الجهاز. */
const REMEMBERED = new Map<string, ListFilterState>();

/**
 * فلتر القائمة. `memory`: اسم الشاشة — الفلتر يفضل محفوظ طول الجلسة، فالرجوع للقائمة
 * يلاقيها زى ما سيبتها. `initialQuery` (من البحث الموحّد) يغلب المحفوظ.
 */
export function useListFilter(
  initialQuery = '',
  memory?: string,
): [ListFilterState, (patch: Partial<ListFilterState>) => void] {
  const [state, setState] = useState<ListFilterState>(() =>
    initialQuery
      ? { ...EMPTY_FILTER, query: initialQuery }
      : ((memory ? REMEMBERED.get(memory) : undefined) ?? EMPTY_FILTER),
  );
  return [
    state,
    (patch) =>
      setState((current) => {
        const next = { ...current, ...patch };
        if (memory) REMEMBERED.set(memory, next);
        return next;
      }),
  ];
}

interface Props {
  filter: ListFilterState;
  onChange: (patch: Partial<ListFilterState>) => void;
  /** نص المساعدة فى خانة البحث — يقول للمستخدم بأى حاجة يدوّر. */
  placeholder: string;
  /** يُظهر حقلى «من / إلى» لقوائم المستندات. */
  dates?: boolean;
  /** خيارات الحالة: القيمة الفاضية = الكل. */
  statuses?: readonly { value: string; label: string }[];
  /** اسم الخيار الفاضى — «كل الحالات» إلا لو الشاشة تقصر الافتراضى على حالة. */
  emptyStatusLabel?: string;
  shown: number;
  total: number;
}

/**
 * شريط البحث فوق أى قائمة: خانة بحث، وفترة وحالة اختياريتان، وعدد الظاهر من الكل.
 * التصفية نفسها فى الشاشة بـ `matchesSearch` — هذا المكوّن يمسك المدخلات فقط.
 */
export default function ListFilter({
  filter,
  onChange,
  placeholder,
  dates = false,
  statuses,
  emptyStatusLabel = 'كل الحالات',
  shown,
  total,
}: Props) {
  const active = filter.query || filter.from || filter.to || filter.status;
  return (
    <div className="list-filter" role="search">
      <input
        type="search"
        value={filter.query}
        onChange={(event) => onChange({ query: event.target.value })}
        placeholder={placeholder}
        aria-label="بحث"
      />
      {dates && (
        <>
          <label>
            من
            <input
              type="date"
              value={filter.from}
              onChange={(event) => onChange({ from: event.target.value })}
            />
          </label>
          <label>
            إلى
            <input
              type="date"
              value={filter.to}
              onChange={(event) => onChange({ to: event.target.value })}
            />
          </label>
        </>
      )}
      {statuses && (
        <select
          value={filter.status}
          onChange={(event) => onChange({ status: event.target.value })}
          aria-label="الحالة"
        >
          <option value="">{emptyStatusLabel}</option>
          {statuses.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      )}
      {active && (
        <>
          <span className="label">
            {shown} من {total}
          </span>
          <button
            type="button"
            className="link"
            onClick={() => onChange({ query: '', from: '', to: '', status: '' })}
          >
            مسح البحث
          </button>
        </>
      )}
    </div>
  );
}
